import { useState } from "react";
import { motion } from "framer-motion";
import { Cpu, Fingerprint, Radar, Workflow, Bot, Coins, ArrowRight } from "lucide-react";
import { ASSETS, BUY_URL, DEXSCREENER_URL } from "@/config";
import ContractChip from "@/components/ContractChip";
import { Corners, GlowButton, HUDPanel, Reveal, SectionHeader, Tag } from "@/components/ui-kit";

const TICKER_ITEMS = [
  "AGENTS ONLINE 1,284", "MACHINES ACTIVE 3,912", "TASKS PROCESSING 427",
  "SETTLEMENT LAYER ARC", "UNIT OF ACCOUNT USDC", "NETWORK STATUS ONLINE",
];

const PIPELINE = [
  { icon: Fingerprint, num: "01", title: "IDENTITY", desc: "Every machine can have a persistent identity.", telemetry: "DID REGISTRY // PERSISTENT" },
  { icon: Radar, num: "02", title: "DISCOVERY", desc: "Agents discover machines based on capabilities.", telemetry: "CAPABILITY INDEX // LIVE" },
  { icon: Workflow, num: "03", title: "COORDINATION", desc: "AI agents coordinate tasks and resources.", telemetry: "TASK ROUTER // AUTONOMOUS" },
  { icon: Bot, num: "04", title: "EXECUTION", desc: "Robots perform physical work.", telemetry: "ACTUATION // VERIFIED" },
  { icon: Coins, num: "05", title: "SETTLEMENT", desc: "Economic activity can settle through ARC.", telemetry: "ARC // USDC RAIL" },
];

const DIAGRAM = [
  { label: "HUMAN", sub: "defines the objective" },
  { label: "AI AGENT", sub: "coordinates execution" },
  { label: "MACHINE NETWORK", sub: "discovers and assigns" },
  { label: "TASK EXECUTION", sub: "physical work performed" },
  { label: "ARC", sub: "settlement environment" },
  { label: "SETTLEMENT", sub: "value exchanged in USDC" },
];

const HomePage = () => {
  const [m, setM] = useState({ x: 0, y: 0 });
  const onMove = (e) => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const r = e.currentTarget.getBoundingClientRect();
    setM({ x: (e.clientX - r.left) / r.width - 0.5, y: (e.clientY - r.top) / r.height - 0.5 });
  };
  const layer = (fx, fy) => ({
    transform: `translate3d(${m.x * fx}px, ${m.y * fy}px, 0)`,
    transition: "transform 0.5s cubic-bezier(0.2, 0.8, 0.2, 1)",
  });

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-center overflow-hidden scanlines" onMouseMove={onMove} aria-label="ARCMECH hero">
        <div className="absolute inset-0 grid-bg" aria-hidden="true" />
        <div className="absolute inset-0" style={layer(-10, -6)} aria-hidden="true">
          <img src={ASSETS.commandBg} alt="" className="w-full h-full object-cover opacity-40" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-b from-arc-black/70 via-arc-black/40 to-arc-black" />
        </div>
        <div className="absolute inset-0 pointer-events-none" style={layer(-22, -14)} aria-hidden="true">
          <div className="absolute top-[18%] left-[6%] w-40 h-40 border border-arc-electric/20 rounded-full" />
          <div className="absolute top-[18%] left-[6%] w-40 h-40 border border-arc-cyan/10 rounded-full scale-125 anim-spin-slow" style={{ transformOrigin: "center" }} />
          <div className="absolute bottom-[20%] right-[8%] w-64 h-64 border border-arc-electric/15 rounded-full anim-spin-slow-rev" />
        </div>
        {/* floating telemetry */}
        <div className="absolute inset-0 pointer-events-none hidden md:block" style={layer(-34, -22)} aria-hidden="true">
          <div className="absolute top-[24%] right-[10%] font-mono text-[10px] tracking-[0.25em] text-arc-cyan/60 anim-float">UPLINK // STABLE</div>
          <div className="absolute bottom-[30%] left-[8%] font-mono text-[10px] tracking-[0.25em] text-arc-cyan/60 anim-float" style={{ animationDelay: "1.2s" }}>SWARM.SYNC // OK</div>
          <div className="absolute top-[55%] right-[22%] font-mono text-[10px] tracking-[0.25em] text-arc-silver/40 anim-float" style={{ animationDelay: "2.1s" }}>PKT 0x3F · 12ms</div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-28 pb-20 w-full grid lg:grid-cols-2 gap-12 items-center">
          <div className="min-w-0" style={layer(-6, -4)}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="eyebrow mb-5 flex items-center gap-3 flex-wrap">
                <span className="w-8 h-px bg-arc-electric inline-block" />ROBOTIC AI · AUTONOMOUS AGENTS · MACHINE ECONOMY
              </div>
              <h1 className="font-display text-[29px] sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-arc-white leading-[1.06]">
                THE<br />AUTONOMOUS<br />MACHINE<br /><span className="text-arc-electric text-glow">ECONOMY.</span>
              </h1>
              <p className="mt-4 font-display text-lg sm:text-xl text-arc-silver/90 tracking-wide">Machines That Think. Machines That Transact.</p>
              <p className="mt-5 max-w-xl text-sm sm:text-base text-arc-silver/70 leading-relaxed">
                ARCMECH explores a machine economy where AI agents coordinate robots, execute physical tasks, and interact with digital services through autonomous economic infrastructure.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <GlowButton to="/network" testId="hero-enter-network" cursor="TARGET">ENTER THE NETWORK<ArrowRight size={14} /></GlowButton>
                <GlowButton to="/thesis" variant="ghost" testId="hero-explore-thesis" cursor="OPEN">EXPLORE THESIS</GlowButton>
                <GlowButton href={BUY_URL} variant="cyan" testId="hero-buy" cursor="TARGET">BUY $ARCMECH</GlowButton>
              </div>
            </motion.div>
          </div>
          <motion.div
            className="relative min-w-0"
            style={layer(-16, -10)}
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.15 }}
          >
            <div className="relative hud-panel p-2" data-testid="hero-visual">
              <Corners />
              <img src={ASSETS.banner} alt="ARCMECH autonomous machine network banner" className="w-full h-auto" loading="eager" />
              <div className="absolute inset-2 pointer-events-none border border-arc-cyan/10" aria-hidden="true" />
              <div className="absolute bottom-4 left-4 font-mono text-[9px] tracking-[0.3em] text-arc-cyan/70">VISUAL // ORBITAL COMMAND · SECTOR VIEW</div>
            </div>
          </motion.div>
        </div>
        <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-arc-black to-transparent pointer-events-none" aria-hidden="true" />
      </section>

      {/* TICKER */}
      <div className="border-y border-arc-blue/15 bg-arc-navy/50 overflow-hidden" aria-label="Simulated telemetry ticker">
        <div className="flex whitespace-nowrap anim-marquee py-2.5 w-max">
          {[...TICKER_ITEMS, ...TICKER_ITEMS, ...TICKER_ITEMS, ...TICKER_ITEMS].map((t, i) => (
            <span key={i} className="font-mono text-[10px] tracking-[0.3em] text-arc-silver/50 mx-6">
              <span className="text-arc-cyan mr-2">▸</span>{t}
            </span>
          ))}
        </div>
      </div>

      {/* FROM MACHINES TO ECONOMIC AGENTS */}
      <section className="relative py-24 px-5 sm:px-8 max-w-7xl mx-auto">
        <SectionHeader
          index="SYS.01 // THESIS"
          title={"FROM MACHINES\nTO ECONOMIC AGENTS."}
          subtitle="Robotics gives machines physical capability. AI gives them intelligence. Autonomous economic infrastructure gives them the ability to coordinate resources and exchange value."
        />
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div className="space-y-0">
            {DIAGRAM.map((n, i) => (
              <Reveal key={n.label} delay={i * 0.08}>
                <div className="flex items-stretch gap-5">
                  <div className="flex flex-col items-center">
                    <div className={`w-3 h-3 mt-5 rotate-45 ${i === DIAGRAM.length - 1 ? "bg-arc-green" : "bg-arc-electric"}`} aria-hidden="true" />
                    {i < DIAGRAM.length - 1 && <div className="w-px flex-1 bg-gradient-to-b from-arc-electric/60 to-arc-electric/10" aria-hidden="true" />}
                  </div>
                  <div className="hud-panel relative flex-1 mb-3 px-5 py-4" data-testid={`diagram-node-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-display font-bold tracking-wider text-arc-white">{n.label}</div>
                      <div className="font-mono text-[10px] text-arc-electric/70 tracking-[0.25em]">{String(i + 1).padStart(2, "0")}</div>
                    </div>
                    <div className="text-sm text-arc-silver/60 mt-0.5">{n.sub}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="lg:sticky lg:top-24 space-y-6">
            <div className="relative hud-panel p-2">
              <Corners />
              <img src={ASSETS.banner} alt="ARCMECH orbital infrastructure with drones and rovers" className="w-full h-auto" loading="lazy" />
            </div>
            <p className="text-sm text-arc-silver/60 leading-relaxed border-l-2 border-arc-electric/50 pl-4">
              The human defines the objective. The AI agent coordinates execution. The machine performs the physical work. ARC provides the settlement environment — and USDC can act as the machine economy's unit of account.
            </p>
            <GlowButton to="/thesis" variant="ghost" testId="diagram-thesis-link" cursor="OPEN">READ THE FULL THESIS<ArrowRight size={14} /></GlowButton>
          </Reveal>
        </div>
      </section>

      {/* HOW ARCMECH WORKS */}
      <section className="relative py-24 px-5 sm:px-8 bg-arc-navy/40 grid-bg">
        <div className="max-w-7xl mx-auto">
          <SectionHeader index="SYS.02 // PIPELINE" title="HOW ARCMECH WORKS" subtitle="A five-stage autonomous pipeline — from persistent machine identity to economic settlement." />
          <div className="relative grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4">
            <div className="hidden xl:block absolute top-1/2 inset-x-8 h-px bg-gradient-to-r from-arc-electric/0 via-arc-electric/40 to-arc-electric/0" aria-hidden="true" />
            {PIPELINE.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.1} className="relative">
                <div data-testid={`pipeline-card-${s.num}`} data-cursor="SCAN" className="hud-panel relative p-5 h-full group hover:border-arc-electric/50 transition-colors duration-300">
                  <Corners className="border-arc-electric/0 group-hover:border-arc-electric/70 transition-colors duration-300" />
                  <div className="flex items-center justify-between mb-4">
                    <s.icon size={22} className="text-arc-electric" aria-hidden="true" />
                    <span className="font-mono text-2xl font-bold text-arc-silver/15">{s.num}</span>
                  </div>
                  <h3 className="font-display text-lg font-bold tracking-wider text-arc-white">{s.title}</h3>
                  <p className="mt-2 text-sm text-arc-silver/60 leading-relaxed">{s.desc}</p>
                  <div className="mt-4 pt-3 border-t border-arc-blue/10 font-mono text-[9px] tracking-[0.2em] text-arc-cyan/50">{s.telemetry}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HUD STRIP */}
      <section className="relative py-24 px-5 sm:px-8 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-3 gap-4">
          <Reveal><HUDPanel testId="hud-machine-status" title="Machine Status" rows={[["STATUS", "● ONLINE", "green"], ["ID", "MECH-031"], ["TYPE", "ROVER"], ["TASK", "INSPECTION"], ["REP", "97.2%", "cyan"]]} /></Reveal>
          <Reveal delay={0.1}><HUDPanel testId="hud-agent-status" title="Agent Status" rows={[["STATUS", "● ONLINE", "green"], ["ID", "AGENT-017"], ["TYPE", "COORDINATOR"], ["TASKS", "184"], ["BALANCE", "18.24 USDC", "cyan"]]} /></Reveal>
          <Reveal delay={0.2}><HUDPanel testId="hud-network-status" title="Network Status" rows={[["LAYER", "ARC"], ["UNIT", "USDC"], ["MODE", "AUTONOMOUS"], ["DATA", "SIMULATED", "cyan"], ["UPTIME", "CONTINUOUS"]]} /></Reveal>
        </div>
      </section>

      {/* TOKEN PANEL */}
      <section className="relative py-24 px-5 sm:px-8 bg-arc-navy/40">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="eyebrow mb-3">SYS.03 // ECOSYSTEM TOKEN</div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-arc-white uppercase">$ARCMECH — THE ECOSYSTEM TOKEN</h2>
            <p className="mt-4 text-sm sm:text-base text-arc-silver/70 leading-relaxed max-w-xl">
              $ARCMECH is the ecosystem token of the autonomous machine economy on ARC. The contract is live — always verify the address via the official X channel before transacting.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Tag tone="silver">TOKEN · $ARCMECH</Tag>
              <Tag tone="silver">NETWORK · ARC</Tag>
              <ContractChip />
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <GlowButton href={BUY_URL} testId="token-buy-button" cursor="TARGET">BUY $ARCMECH</GlowButton>
              {DEXSCREENER_URL ? (
                <GlowButton href={DEXSCREENER_URL} variant="ghost" testId="token-chart-button" cursor="OPEN">VIEW CHART</GlowButton>
              ) : (
                <GlowButton variant="ghost" testId="token-chart-button" className="opacity-60 cursor-not-allowed" cursor="SCAN">CHART — COMING SOON</GlowButton>
              )}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative hud-panel p-8 flex flex-col items-center scanlines">
              <Corners />
              <img src={ASSETS.logo} alt="ARCMECH token emblem" className="w-52 h-auto anim-float" loading="lazy" />
              <div className="mt-6 w-full grid grid-cols-2 gap-3 font-mono text-[11px] tracking-[0.15em]">
                <div className="border border-arc-blue/15 p-3"><div className="text-arc-silver/40 text-[9px] tracking-[0.3em] mb-1">TOKEN</div><div className="text-arc-white">$ARCMECH</div></div>
                <div className="border border-arc-blue/15 p-3"><div className="text-arc-silver/40 text-[9px] tracking-[0.3em] mb-1">NETWORK</div><div className="text-arc-white">ARC</div></div>
                <div className="border border-arc-blue/15 p-3"><div className="text-arc-silver/40 text-[9px] tracking-[0.3em] mb-1">CONTRACT</div><div className="text-arc-cyan">0x5e01…f5aD</div></div>
                <div className="border border-arc-blue/15 p-3"><div className="text-arc-silver/40 text-[9px] tracking-[0.3em] mb-1">BUY</div><div className="text-arc-cyan">ARGUS</div></div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="relative py-28 px-5 sm:px-8 overflow-hidden scanlines">
        <div className="absolute inset-0" aria-hidden="true">
          <img src={ASSETS.commandBg} alt="" className="w-full h-full object-cover opacity-25" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-b from-arc-black via-arc-black/60 to-arc-black" />
        </div>
        <div className="relative max-w-3xl mx-auto text-center">
          <Reveal>
            <Cpu className="mx-auto text-arc-electric mb-6" size={28} aria-hidden="true" />
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-arc-white uppercase">Enter the operating system of the machine economy.</h2>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <GlowButton to="/terminal" testId="cta-terminal" cursor="INPUT">OPEN TERMINAL</GlowButton>
              <GlowButton to="/launch" variant="ghost" testId="cta-launch" cursor="OPEN">LAUNCH STATUS</GlowButton>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
